import { NavLink, useLocation } from "react-router-dom";
import {
  FaHome,
  FaUserAlt,
  FaCalendarCheck,
  FaUsers,
  FaUserCheck,
  FaHandshake,
  FaPhoneSquareAlt,
  FaBell,
} from "react-icons/fa";
import {
  MdAddBox,
  MdAddCircleOutline,
  MdBarChart,
  MdDashboardCustomize,
  MdOutlineSecurity,
  MdPerson,
  MdSettings,
} from "react-icons/md";
import { useEffect, useMemo, useState } from "react";
import Navbar from "../../Navbar/Navbar";
import AuthService from "../../../modules/Admin/services/AuthService";
import { isModuleEnabled, ensureModuleAccessLoaded } from "../../utils/moduleAccessStore";
import { getSidebarBrandTitle } from "../../constants/branding";

const routes = [
  { path: "/dashboard", name: "Dashboard", icon: <FaHome />, moduleKey: "dashboard" },

  { path: "/leads", name: "Leads", icon: <MdAddCircleOutline />, moduleKey: "leads" },
  { path: "/leadassigned", name: "Assigned Leads", icon: <MdAddBox />, moduleKey: "assignedLeads" },
  { path: "/buyers", name: "Buyers", icon: <FaUserCheck />, moduleKey: "buyers" },
  { path: "/kanban", name: "Kanban Pipeline", icon: <MdDashboardCustomize />, moduleKey: "kanban" },
  { path: "/pendingfollowups", name: "Pending FollowUps", icon: <FaUserAlt />, moduleKey: "pendingFollowUps" },

  { path: "/propertylisting", name: "Property Listing", icon: <FaHome />, moduleKey: "propertyListing" },
  { path: "/builders", name: "Builders / Partners", icon: <FaHandshake />, moduleKey: "builders" },
  { path: "/reports", name: "Reports", icon: <MdBarChart />, moduleKey: "reports" },

  { path: "/attendance", name: "My Attendance", icon: <FaCalendarCheck />, moduleKey: "attendance" },
  { path: "/team-attendance", name: "Team Attendance", icon: <FaUsers />, moduleKey: "teamAttendance" },
  {
    name: "Settings",
    icon: <MdSettings />,
    children: [
      { path: "/settings", name: "Workspace", icon: <MdSettings />, moduleKey: "settings" },
      { path: "/pipeline-stages", name: "Pipeline Stages", icon: <MdOutlineSecurity />, moduleKey: "pipelineStages" },
      { path: "/notification-engine", name: "Notifications", icon: <FaBell />, moduleKey: "notificationEngine" },
      { path: "/registeruser", name: "Employees", icon: <MdPerson />, moduleKey: "employees" },
    ],
  },
  { path: "/quicksupport", name: "Quick Support", icon: <FaPhoneSquareAlt />, moduleKey: "quickSupport" },
  // {
  //   path: "/about",
  //   name: "About",
  //   icon: <MdInfo />,
  // },
  // {
  //   path: "/privacy",
  //   name: "Privacy",
  //   icon: <MdPrivacyTip />,
  // },
  // {
  //   path: "/termsandconditions",
  //   name: "Terms and Conditions",
  //   icon: <MdOutlineSecurity />,
  // },
];

const SETTINGS_PATHS = ["/settings", "/pipeline-stages", "/notification-engine", "/registeruser", "/adduser", "/edituser"];

function isRouteVisible(route, roleNum) {
  if (roleNum === 1) return true;
  if (route.children?.length) {
    return route.children.some((c) => !c.moduleKey || isModuleEnabled(c.moduleKey));
  }
  return !route.moduleKey || isModuleEnabled(route.moduleKey);
}

function visibleChildren(route, roleNum) {
  if (!route.children) return [];
  let kids = roleNum === 1 ? route.children : route.children.filter((c) => !c.moduleKey || isModuleEnabled(c.moduleKey));
  if (roleNum !== 1) {
    kids = kids.filter((c) => c.path !== '/notification-engine');
  }
  return kids;
}

const SideBar = ({ children }) => {
  const [isOpen] = useState(true);
  const [menuSearch, setMenuSearch] = useState("");
  const [moduleAccessTick, setModuleAccessTick] = useState(0);
  const location = useLocation();
  const roleNum = parseInt(AuthService.getLoggedInRoleId(), 10);
  const settingsActive = SETTINGS_PATHS.some((p) => location.pathname.startsWith(p));
  const [settingsOpen, setSettingsOpen] = useState(settingsActive);

  useEffect(() => {
    if (settingsActive) setSettingsOpen(true);
  }, [settingsActive]);

  useEffect(() => {
    if (roleNum === 1) return undefined;
    let cancelled = false;
    ensureModuleAccessLoaded().then(() => {
      if (!cancelled) setModuleAccessTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [roleNum]);

  const visibleRoutes = useMemo(() => {
    const base = roleNum === 1 ? routes : routes.filter((r) => isRouteVisible(r, roleNum));
    return base
      .map((r) => {
        if (!r.children) return r;
        return { ...r, children: visibleChildren(r, roleNum) };
      })
      .filter((r) => !r.children || r.children.length > 0);
  }, [roleNum, moduleAccessTick]);

  const filteredRoutes = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return visibleRoutes;
    return visibleRoutes.flatMap((r) => {
      if (r.path) {
        return r.name.toLowerCase().includes(q) ? [r] : [];
      }
      if (!r.children) return [];
      const parentMatch = r.name.toLowerCase().includes(q);
      const kids = visibleChildren(r, roleNum).filter(
        (c) => parentMatch || c.name.toLowerCase().includes(q)
      );
      return kids.length ? [{ ...r, children: kids }] : [];
    });
  }, [visibleRoutes, menuSearch, roleNum]);
  const [agency, setAgency] = useState(null);
  const ClientId = AuthService.getLoggedInClientId();

  useEffect(() => {
    async function getAgencyLogoByClientId() {
      try {
        const response =
          await AuthService.getAgencyLogoAndNameByClientId(ClientId);
        setAgency(response);
      } catch (error) {
        console.error("Error fetching agency:", error);
      }
    }
    getAgencyLogoByClientId();
  }, [ClientId]);

  const closeSidebar = () => {
    document.body.classList.remove("sidebar-open");
  };

  return (
    <>
      <div
        className="sidebar-backdrop"
        onClick={closeSidebar}
        aria-hidden="true"
      />
      <aside
        className="sidenav navbar navbar-vertical navbar-expand-xs border-radius-lg fixed-start ms-2 my-2"
        id="sidenav-main"
      >
        <div className="sidenav-header">
          <i
            className="fas fa-times p-3 cursor-pointer text-white opacity-6 position-absolute end-0 top-0 d-none d-xl-none"
            aria-hidden="true"
            id="iconSidenav"
          ></i>
          <NavLink
            className="navbar-brand nf-sidebar-brand"
            to="/dashboard"
            onClick={closeSidebar}
          >
            <span className="nf-sidebar-brand-name" title={getSidebarBrandTitle(agency)}>
              {getSidebarBrandTitle(agency)}
            </span>
          </NavLink>
        </div>
        <div className="nf-sidebar-search-wrap">
          <div className="nf-sidebar-search">
            <i className="fas fa-search nf-sidebar-search-icon" aria-hidden="true" />
            <input
              type="search"
              className="nf-sidebar-search-input"
              placeholder="Search menu…"
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              aria-label="Search navigation"
            />
          </div>
        </div>
        <hr className="horizontal dark mt-0 mb-2" />
        <div
          className="collapse navbar-collapse w-auto"
          id="sidenav-collapse-main"
        >
          <section className="routes">
            <div className="nf-sidebar-section-label text-uppercase">Menu</div>
            <ul className="navbar-nav">
              {filteredRoutes.map((route, index) => {
                if (route.children?.length) {
                  const kids = route.children;
                  const groupActive = kids.some(
                    (c) => location.pathname === c.path || location.pathname.startsWith(`${c.path}/`)
                  ) || location.pathname.startsWith("/adduser") || location.pathname.startsWith("/edituser");
                  return (
                    <li className="nav-item nf-nav-group" key={`group-${route.name}-${index}`}>
                      <button
                        type="button"
                        className={`nav-link d-flex align-items-center w-100 nf-nav-group-toggle${groupActive ? " active" : ""}`}
                        onClick={() => setSettingsOpen((o) => !o)}
                        aria-expanded={settingsOpen}
                      >
                        <div className="icon me-2">{route.icon}</div>
                        {isOpen && (
                          <>
                            <div className="link_text flex-grow-1 text-start">{route.name}</div>
                            <i className={`fas fa-chevron-${settingsOpen ? "up" : "down"} nf-nav-chevron`} />
                          </>
                        )}
                      </button>
                      {settingsOpen && (
                        <ul className="navbar-nav nf-nav-sub">
                          {kids.map((child) => (
                            <li className="nav-item" key={child.path}>
                              <NavLink
                                to={child.path}
                                className={({ isActive }) =>
                                  `nav-link d-flex align-items-center nf-nav-sub-link${isActive || (child.path === "/registeruser" && (location.pathname.startsWith("/adduser") || location.pathname.startsWith("/edituser"))) ? " active" : ""}`
                                }
                                onClick={closeSidebar}
                              >
                                <div className="icon me-2">{child.icon}</div>
                                {isOpen && <div className="link_text">{child.name}</div>}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li className="nav-item" key={`${route.path}-${index}`}>
                    <NavLink
                      to={route.path}
                      className={({ isActive }) =>
                        `nav-link d-flex align-items-center${isActive ? " active" : ""}`
                      }
                      onClick={closeSidebar}
                    >
                      <div className="icon me-2">{route.icon}</div>
                      {isOpen && <div className="link_text">{route.name}</div>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </aside>
      <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
        <Navbar />
        <div className="container-fluid py-2">{children}</div>
      </main>
    </>
  );
};

export default SideBar;
